import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Phonelink from '@mui/icons-material/Phonelink';
import AdsClick from '@mui/icons-material/AdsClick';
import Insights from '@mui/icons-material/Insights';
import CallToAction from '@mui/icons-material/CallToAction';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';

// Static catalog and shape-normalizer hoisted out of the hook so they are
// referentially stable — required for the `reset` useCallback below to satisfy
// react-hooks/exhaustive-deps without needlessly invalidating every render.
const DEFAULT_PRODUCTS = [
  { id: 'business-phone', active: false },
  { id: 'call-tracking', active: false },
  { id: 'ai-conversation', active: false },
  { id: 'callback', active: false },
  { id: 'phone-numbers', active: false },
  { id: 'voice-traffic', active: false },
  { id: 'omnichannel-chat', active: false },
  { id: 'eniq-ai', active: false },
  { id: 'voicebot', active: false }
];

// Accepts both array and legacy object shape ({ 'business-phone': true }).
const mergeWithDefaults = (savedProducts) => {
  if (!savedProducts) return DEFAULT_PRODUCTS;

  const savedArray = Array.isArray(savedProducts)
    ? savedProducts
    : Object.entries(savedProducts).map(([id, active]) => ({ id, active: Boolean(active) }));

  const merged = savedArray
    .filter(product => product && typeof product.id === 'string')
    .map(product => ({ ...product, active: Boolean(product.active) }));

  DEFAULT_PRODUCTS.forEach(defaultProduct => {
    if (!merged.find(p => p.id === defaultProduct.id)) {
      merged.push(defaultProduct);
    }
  });

  return merged;
};

/**
 * Custom hook for managing products in the pricing calculator
 * @param {Object} initialState - Initial state for product values
 * @returns {Object} - Product management functions and data
 */
const useProducts = (initialState = {}) => {
  const { t } = useTranslation();

  // Product values
  const [businessPhoneUsers, setBusinessPhoneUsersInternal] = useState(initialState.businessPhoneUsers || 3);
  const [callTrackingSessions, setCallTrackingSessionsInternal] = useState(initialState.callTrackingSessions || 100);
  const [aiCalls, setAiCalls] = useState(initialState.aiCalls || 100);
  const [callbackSessions, setCallbackSessions] = useState(initialState.callbackSessions || 100);
  const [voiceTrafficMinutes, setVoiceTrafficMinutes] = useState(initialState.voiceTrafficMinutes || 1000);
  const [omnichannelChatUsers, setOmnichannelChatUsers] = useState(initialState.omnichannelChatUsers || 3);
  const [manualPackageSelection, setManualPackageSelection] = useState(initialState.manualPackageSelection || false);

  // Wrapper functions to update parent state if available
  const setBusinessPhoneUsers = useCallback((value) => {
    setBusinessPhoneUsersInternal(value);
    if (initialState.setBusinessPhoneUsers) {
      initialState.setBusinessPhoneUsers(value);
    }
  }, [initialState]);

  const setCallTrackingSessions = useCallback((value) => {
    setCallTrackingSessionsInternal(value);
    if (initialState.setCallTrackingSessions) {
      initialState.setCallTrackingSessions(value);
    }
  }, [initialState]);

  const [selectedProducts, setSelectedProducts] = useState(
    mergeWithDefaults(initialState.selectedProducts)
  );

  const reset = useCallback((newState = {}) => {
    setBusinessPhoneUsersInternal(newState.businessPhoneUsers || 3);
    setCallTrackingSessionsInternal(newState.callTrackingSessions || 100);
    setAiCalls(newState.aiCalls || 5000);
    setCallbackSessions(newState.callbackSessions || 100);
    setVoiceTrafficMinutes(newState.voiceTrafficMinutes || 1000);
    setOmnichannelChatUsers(newState.omnichannelChatUsers || 3);
    setManualPackageSelection(newState.manualPackageSelection || false);
    setSelectedProducts(mergeWithDefaults(newState.selectedProducts));
  }, []);

  // Helper function to check if a product is selected
  const isProductSelected = useCallback((productId) => {
    const product = selectedProducts.find(p => p.id === productId);
    return product ? product.active : false;
  }, [selectedProducts]);
  
  // Toggle product selection
  const toggleProduct = useCallback((productId) => {
    setSelectedProducts(prev =>
      prev.map(p => p.id === productId ? { ...p, active: !p.active } : p)
    );
  }, []);
  
  // Product options data - always show all products
  const getProductOptions = useCallback(() => {
    return [
      { id: 'business-phone', label: t('calculator.businessPhone', 'Business Phone Users'), icon: Phonelink },
      { id: 'call-tracking', label: t('calculator.callTracking', 'Call Tracking Sessions / Day'), icon: AdsClick },
      { id: 'ai-conversation', label: t('calculator.aiConversation', 'AI Conversation Intelligence'), icon: Insights },
      { id: 'callback', label: t('calculator.callback', 'Callback Widget (Callbacks)'), icon: CallToAction },
      { id: 'phone-numbers', label: t('calculator.phoneNumbers', 'Phone Numbers'), icon: LocalPhoneIcon },
      { id: 'voice-traffic', label: t('calculator.voiceTraffic', 'Voice Traffic'), icon: PhoneInTalkIcon },
      { id: 'omnichannel-chat', label: t('calculator.omnichannelChat', 'Omnichannel Chat'), icon: ChatBubbleOutlineIcon },
      { id: 'eniq-ai', label: t('calculator.eniqAiLabel', 'Eniq AI'), icon: SmartToyIcon },
      { id: 'voicebot', label: t('calculator.voicebotLabel', 'VoiceBot'), icon: RecordVoiceOverIcon }
    ];
  }, [t]);
  
  // Selected products data for rendering
  const selectedProductsData = useMemo(() => {
    const getActiveState = (productId) => {
      const product = selectedProducts.find(p => p.id === productId);
      return product ? product.active : false;
    };

    return [
      {
        id: 'business-phone',
        label: t('calculator.businessPhone', 'Business Phone Users'),
        icon: Phonelink,
        value: businessPhoneUsers,
        setValue: setBusinessPhoneUsers,
        min: 3,
        max: 1000,
        description: t('calculator.businessPhoneDesc', 'Number of users needing business phone features'),
        active: getActiveState('business-phone')
      },
      {
        id: 'call-tracking',
        label: t('calculator.callTracking', 'Call Tracking Sessions / Day'),
        icon: AdsClick,
        value: callTrackingSessions,
        setValue: setCallTrackingSessions,
        min: 100,
        max: 100000,
        description: t('calculator.callTrackingDesc', 'Daily website sessions'),
        active: getActiveState('call-tracking')
      },
      {
        id: 'ai-conversation',
        label: t('calculator.aiConversation', 'AI Conversation Intelligence'),
        icon: Insights,
        value: aiCalls,
        setValue: setAiCalls,
        min: 100,
        max: 10000,
        description: t('calculator.aiConversationDesc', 'Number of minutes to analyze per month'),
        active: getActiveState('ai-conversation')
      },
      {
        id: 'callback',
        label: t('calculator.callback', 'Callback Widget (Callbacks)'),
        icon: CallToAction,
        value: callbackSessions,
        setValue: setCallbackSessions,
        min: 100,
        max: 10000,
        description: t('calculator.callbackDesc', 'Estimated monthly callback requests'),
        active: getActiveState('callback')
      },
      {
        id: 'phone-numbers',
        label: t('calculator.phoneNumbers', 'Phone Numbers'),
        icon: LocalPhoneIcon,
        value: null, // No slider value
        setValue: () => {}, // No setter
        min: 0,
        max: 0,
        description: t('calculator.phoneNumbersDesc', 'Purchase and manage phone numbers'),
        active: getActiveState('phone-numbers')
      },
      {
        id: 'voice-traffic',
        label: t('calculator.voiceTraffic', 'Voice Traffic'),
        icon: PhoneInTalkIcon,
        value: voiceTrafficMinutes,
        setValue: setVoiceTrafficMinutes,
        min: 100,
        max: 50000,
        manualPackageSelection: manualPackageSelection,
        setManualPackageSelection: setManualPackageSelection,
        description: t('calculator.voiceTrafficDesc', 'Number of voice traffic minutes per month'),
        active: getActiveState('voice-traffic')
      },
      {
        id: 'omnichannel-chat',
        label: t('calculator.omnichannelChat', 'Omnichannel Chat'),
        icon: ChatBubbleOutlineIcon,
        value: omnichannelChatUsers,
        setValue: setOmnichannelChatUsers,
        min: 3,
        max: 1000,
        description: t('calculator.omnichannelChatDesc', 'Users for online chat + messengers'),
        active: getActiveState('omnichannel-chat')
      },
      {
        id: 'eniq-ai',
        label: t('calculator.eniqAiLabel', 'Eniq AI'),
        icon: SmartToyIcon,
        value: null, // Managed by CalculatorContext
        setValue: () => {}, // Managed by CalculatorContext
        min: 1000,
        max: 20000,
        description: t('calculator.eniqAiDesc', 'AI-powered conversation analysis'),
        active: getActiveState('eniq-ai')
      },
      {
        id: 'voicebot',
        label: t('calculator.voicebotLabel', 'VoiceBot'),
        icon: RecordVoiceOverIcon,
        value: null, // Managed by CalculatorContext
        setValue: () => {}, // Managed by CalculatorContext
        min: 500,
        max: 20000,
        description: t('calculator.voicebotDesc', 'AI-powered automated calls'),
        active: getActiveState('voicebot')
      }
    ];
  }, [
    t,
    businessPhoneUsers,
    callTrackingSessions,
    aiCalls,
    callbackSessions,
    voiceTrafficMinutes,
    omnichannelChatUsers,
    manualPackageSelection,
    selectedProducts
  ]);
  
  return {
    // Product values
    businessPhoneUsers,
    setBusinessPhoneUsers,
    callTrackingSessions,
    setCallTrackingSessions,
    aiCalls,
    setAiCalls,
    callbackSessions,
    setCallbackSessions,
    voiceTrafficMinutes,
    setVoiceTrafficMinutes,
    omnichannelChatUsers,
    setOmnichannelChatUsers,
    manualPackageSelection,
    setManualPackageSelection,
    
    // Product selection
    selectedProducts,
    isProductSelected,
    toggleProduct,
    
    // Product data
    productOptions: getProductOptions, // Now a function
    selectedProductsData,

    // Reset function
    reset
  };
};

export default useProducts;
